import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

/**
 * Applied until the feed lands (and whenever it fails), so a quantity prompt is never left without
 * a ceiling. Must stay in sync with `MobileAppService.DefaultMaxQty` on the backend.
 */
export const DEFAULT_MAX_QTY = 99999;

/** `MOBILE`/`MaxQty` uses 0 to mean "no ceiling at all". */
export const MAX_QTY_UNLIMITED = 0;

interface AbpResponse<T> {
  result: T;
  success: boolean;
}

interface MobileConfigResult {
  readonly maxQty: number;
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly http = inject(HttpClient);

  private readonly _maxQty = signal<number>(DEFAULT_MAX_QTY);
  private readonly _loaded = signal(false);

  private inFlight: Promise<void> | null = null;
  private generation = 0;

  /**
   * Operator-visible quantity ceiling from `sys_system_parameters` (`MOBILE`/`MaxQty`), or
   * {@link DEFAULT_MAX_QTY} until the feed lands. `0` means unlimited — read it through
   * {@link exceedsMax} rather than comparing directly.
   */
  readonly maxQty = computed(() => this._maxQty());

  /** True once the backend value has been applied at least once this session. */
  readonly loaded = computed(() => this._loaded());

  /**
   * Idempotent: joins an in-flight request, no-ops once the value has been loaded, otherwise
   * fetches. Called on startup (root component, on every authenticated boot — fresh sign-in or
   * PWA relaunch into an existing session) and safe to call from anywhere else.
   *
   * Never rejects: a transport/permission failure leaves the previously applied value in place
   * (or {@link DEFAULT_MAX_QTY} on a cold start), because a workflow screen must stay usable
   * when only the config feed is down.
   */
  ensureLoaded(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this._loaded()) return Promise.resolve();
    return this.fetch();
  }

  /** Forces a fresh fetch, joining an in-flight request if there is one. */
  reload(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    return this.fetch();
  }

  /**
   * Resets to the built-in default (logout / session expiry / tenant switch). Also invalidates any
   * in-flight fetch via the generation counter so a response for a pre-clear request — potentially
   * another tenant's parameter — can never write state after this runs.
   */
  clear(): void {
    this.generation++;
    this.inFlight = null;
    this._maxQty.set(DEFAULT_MAX_QTY);
    this._loaded.set(false);
  }

  /**
   * True when `qty` is above the configured ceiling. Non-finite input is not this guard's
   * concern (callers reject it with their own "invalid quantity" message) and reads as
   * "not over the max".
   */
  exceedsMax(qty: number): boolean {
    const max = this._maxQty();
    if (max === MAX_QTY_UNLIMITED) return false;
    if (!Number.isFinite(qty)) return false;
    return qty > max;
  }

  private fetch(): Promise<void> {
    const generationAtStart = this.generation;

    const promise = firstValueFrom(
      this.http
        .get<AbpResponse<MobileConfigResult>>(`${environment.apiUrl}/api/services/app/Mobile/GetAppConfig`)
        .pipe(map((r) => r.result?.maxQty)),
    )
      .then((maxQty) => {
        if (generationAtStart !== this.generation) return;
        // A malformed/absent payload is treated like a failed fetch: keep the safe default
        // rather than applying NaN/undefined as a ceiling.
        if (typeof maxQty !== 'number' || !Number.isInteger(maxQty) || maxQty < 0) return;
        this._maxQty.set(maxQty);
        this._loaded.set(true);
      })
      .catch(() => {
        // Intentionally swallowed — see ensureLoaded().
      });

    this.inFlight = promise;
    void promise.finally(() => {
      if (this.inFlight === promise) this.inFlight = null;
    });
    return promise;
  }
}
